// =============================================================================
// FIELD PRACTICES — the LOOP 3 SUBSTRATE (W1-6a, launch-week-briefs.md §A +
// the LOOPS-FIRST LAW block). A verified-source lake, same family as
// platform_algorithm_rules / au_visa_data / craft_methods: cross-user (no
// userId), research-tuned, cited-or-rejected at the boundary.
//
// WHAT THIS IS: the tailoring surface's per-field "how it's actually done"
// knowledge — cover-letter length conventions, CV conventions, opening-style
// conventions — split US vs AU (and any future locale) so TailoringService
// stops hardcoding "250-400 words, AU register" as a prompt literal and
// instead READS it, sourced and dated.
//
// THE LOOP THIS IS THE LEDGER FOR (not a static table): a research crew
// (future automation, or Jason by hand via WebFetch+cite today) writes rows
// here with a REAL sourceRef; tailoring reads current state per (field,
// practiceKind); staleAfter marks when a row's guidance should be
// re-verified (conventions drift — a "2026 word count" claim ages). The
// STALENESS read (FieldPracticesService.ts) is what closes the loop: a row
// past staleAfter is served WITH {stale:true} so a future research pass has
// a worklist, rather than a table nobody ever revisits. This file is the
// substrate; the loop's engine (the re-verify crew) is a LATER phase per the
// LOOPS-FIRST LAW ("substrate FIRST, factors as READERS").
//
// NO INVENTION, enforced in CODE at the boundary (not merely documented
// here): sourceRef is NOT NULL at the column level AND the write path
// (FieldPracticesService's seed/insert helper) rejects a candidate row whose
// sourceRef has no url — citation-or-reject, mirroring company_targets'
// assertSignalsGrounded posture (same reasoning: a Postgres CHECK over jsonb
// shape is impractical, so the gate lives in the service, tested directly).
//
// FAIL-OPEN ON THE READ SIDE: an unseeded field is never a hard failure —
// FieldPracticesSource.forField returns null for "no guidance on record",
// and every consumer (deriveRegister callers, the tailoring composer) treats
// null as "use the existing in-code default", never an invented specific.
// This is the DUAL posture the brief calls for: WRITE fails closed (no
// citation, no row), READ fails open (no row, no crash — just no override).
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool,
// mirrored here in Drizzle and exported from index.ts. SQL file:
// docs/design/w1-6-tailoring-refinements-neon.sql (never run by this code).
// =============================================================================

import { boolean, index, jsonb, pgTable, text, timestamp, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The three practice kinds this brief seeds. Not a DB enum (the craft_methods /
 *  platform_algorithm precedent: a varchar + application-level closed set, so a
 *  new kind is a data row, not a migration). */
export type FieldPracticeKind = 'cover_tone' | 'cv_convention' | 'opening_style';

/** Provenance for ONE practice row. REQUIRED at the type level — mirrors
 *  CompanyTargetSignal's sourceUrl-required shape (company_targets.ts) — so a
 *  caller cannot even construct a candidate row without a citation without
 *  lying to the type system. The runtime gate (assertSourceRefGrounded in
 *  FieldPracticesService.ts) is the REAL enforcement (a hand-authored seed or a
 *  future research-crew payload is untyped at the boundary), but the type still
 *  documents the invariant. */
export interface FieldPracticeSourceRef {
  url: string;
  title?: string;
  /** ISO timestamp the source was actually fetched/quoted, when known. */
  retrievedAt?: string;
}

export const fieldPractices = pgTable(
  'field_practices',
  {
    id: id(),
    /** The domain this guidance applies to — free-text today (e.g. 'US',
     *  'AU', 'US-tech', 'AU-mining'), same provenance-not-lookup shape
     *  company_targets.sector/geo already establish (a locale/sector PACK
     *  lookup table is a LATER extension, not this table's job to pre-empt). */
    field: varchar('field', { length: 120 }).notNull(),
    practiceKind: varchar('practice_kind', { length: 24 }).$type<FieldPracticeKind>().notNull(),
    /** The guidance itself — a human-readable directive the composer's prompt
     *  can inject verbatim or paraphrase (e.g. "250-400 words, 3-4 short
     *  paragraphs, half-to-one page — US convention per Indeed's career
     *  guide"). Free text, not structured, because the guidance format varies
     *  meaningfully across the three kinds (a word-count rule reads
     *  differently from an opening-style rule). */
    guidance: text('guidance').notNull(),
    /** NOT NULL — the citation-or-reject boundary (NO INVENTION, in code).
     *  jsonb (not text) so a reader gets a real object back, matching the
     *  house convention (CompanyTargetSignal.sourceUrl, PlatformRuleSource) —
     *  a Postgres CHECK over jsonb shape is impractical (same reasoning
     *  company_targets.ts documents), so the gate lives in the service,
     *  tested directly (FieldPracticesService's assertSourceRefGrounded). */
    sourceRef: jsonb('source_ref').$type<FieldPracticeSourceRef>().notNull(),
    verifiedAt: timestamp('verified_at', { withTimezone: true }),
    /** When this row's guidance should be treated as due for re-verification.
     *  Null = no staleness policy set for this row (never treated as stale by
     *  the read helper — see FieldPracticesService.isStale). Distinct from
     *  lakeProvenance's verifyState band (seed/researched/…): staleAfter is a
     *  DATE the loop's ledger can act on, not a coarse confidence label. */
    staleAfter: timestamp('stale_after', { withTimezone: true }),
    /** Soft-disable without deleting (nothing-unrecoverable law): a superseded
     *  or retracted row is flipped to false, never dropped. Defaults true so
     *  a normal insert is immediately live. */
    active: boolean('active').notNull().default(true),
    ...timestamps,
  },
  (t) => ({
    fieldKindIdx: index('field_practices_field_kind_idx').on(t.field, t.practiceKind),
    activeIdx: index('field_practices_active_idx').on(t.active),
    // Dedup key for the seed/re-seed path: one guidance row per (field, kind,
    // guidance) so a re-run of the same cited claim upserts rather than
    // duplicating. Mirrors platform_algorithm_rules' rule_key uniqueness
    // intent, scoped narrower since this table has no single slug column.
    fieldKindGuidanceIdx: uniqueIndex('field_practices_field_kind_guidance_idx').on(
      t.field,
      t.practiceKind,
      t.guidance,
    ),
  }),
);
